import React, { useState } from 'react';
import {
  Box,
  Button,
  Modal,
  Typography,
  TextField,
  Grid,
  IconButton,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { supabase } from '../bd/supabase';

export default function ModalSuscripcion({ open, handleClose, onSubmit }) {
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    correo: '',
    empresa: '',
    puesto: '',
    pais: '',
    estado: '',
    conociste: '',
  });

  const [errors, setErrors] = useState({});
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [aceptaAviso, setAceptaAviso] = useState(false);
  const [avisoError, setAvisoError] = useState(false);


  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    const newErrors = { ...errors };

    if (!value.trim()) {
      newErrors[name] = 'Este campo es requerido';
    } else {
      delete newErrors[name];

      if (name === 'correo' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        newErrors[name] = 'Correo electrónico inválido';
      }

      if (name === 'telefono' && (!/^\d+$/.test(value) || value.length < 10)) {
        newErrors[name] = 'Teléfono inválido (solo números, mínimo 10 dígitos)';
      }

      if (name === 'nombre' && value.length < 2) {
        newErrors[name] = 'Nombre muy corto';
      }
    }

    setErrors(newErrors);
  };

  const validarFormulario = () => {
    const nuevosErrores = {};

    if (!formData.nombre.trim() || formData.nombre.length < 2) {
      nuevosErrores.nombre = 'Nombre inválido';
    }

    if (!formData.telefono.trim() || !/^\d+$/.test(formData.telefono) || formData.telefono.length < 8) {
      nuevosErrores.telefono = 'Teléfono inválido (solo números, mínimo 8 dígitos)';
    }

    if (!formData.correo.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.correo)) {
      nuevosErrores.correo = 'Correo electrónico inválido';
    }

    ['empresa', 'puesto', 'pais', 'estado', 'conociste'].forEach((campo) => {
      if (!formData[campo].trim()) {
        nuevosErrores[campo] = 'Este campo es requerido';
      }
    });

    setErrors(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  const triggerDownload = () => {
    const link = document.createElement('a');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubmit = async () => {
    if (!aceptaAviso) {
      setAvisoError(true);
      return;
    }
    if (validarFormulario()) {
      try {
        const { error } = await supabase
          .from('suscripciones')
          .insert([formData]);

        if (error) {
          console.error('Error al guardar en la base de datos: ', error.message);
          return;
        }

        console.log('Guardado en la base de datos');
        sessionStorage.setItem('estaSuscrito', true);
        if (typeof onSubmit === 'function') onSubmit(formData);

        setFormData({
          nombre: '', telefono: '', correo: '', empresa: '',
          puesto: '', pais: '', estado: '', conociste: ''
        });
        handleClose();
        setOpenSnackbar(true);
        triggerDownload();
      } catch (err) {
        console.error('Error inesperado:', err);
      }
    }
  };

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: isMobile ? '90%' : isTablet ? '80%' : 886,
            maxWidth: '95vw',
            maxHeight: '95vh',
            height: isMobile ? 'auto' : 'auto',
            minHeight: isMobile ? 'auto' : 603,
            backgroundColor: '#84BFCB',
            backgroundImage: 'url(https://res.cloudinary.com/dhoyyqszi/image/upload/v1748997386/2d-graphic-wallpaper-with-colorful-grainy-gradients_1_mznzhx.png)',
            backgroundSize: 'contain',
            backgroundPosition: 'center top',
            backgroundRepeat: 'no-repeat',
            borderRadius: '20px',
            boxShadow: 24,
            overflowY: 'auto',
            px: isMobile ? 2 : 4,
            py: isMobile ? 4 : 4,
            '&::-webkit-scrollbar': {
              width: '0.4em'
            },
            '&::-webkit-scrollbar-track': {
              boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,.1)',
              borderRadius: '10px'
            }
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              color: '#fff',
              zIndex: 2,
              backgroundColor: 'rgba(0,0,0,0.1)',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.2)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box
            sx={{
              mt: isMobile ? 0 : '80px',
              display: 'flex',
              justifyContent: 'center',
              mb: isMobile ? 2 : 0
            }}
          >
            <Box sx={{ maxWidth: '600px', width: '100%', px: isMobile ? 1 : 0 }}>
              <Typography
                align="center"
                gutterBottom
                sx={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: isMobile ? '24px' : '32px',
                  fontWeight: 400,
                  color: '#000',
                  lineHeight: 1.2
                }}
              >
                Formulario de{' '}
                <Box
                  component="span"
                  sx={{
                    background: 'linear-gradient(90deg, #0A6274 39%, #13B8DA 100%)',
                    color: '#fff',
                    width: isMobile ? '180px' : '211px',
                    height: isMobile ? '36px' : '42px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '20px',
                    fontWeight: 700,
                    fontSize: isMobile ? '24px' : '32px',
                    fontFamily: 'Poppins, sans-serif',
                    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
                    mt: isMobile ? 1 : 0
                  }}
                >
                  suscripción
                </Box>
              </Typography>

              <Typography
                align="center"
                mb={4}
                sx={{
                  color: '#38383D',
                  fontWeight: 'bold',
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: isMobile ? '14px' : '16px',
                  px: isMobile ? 1 : 0
                }}
              >
                Llena el formulario y nos pondremos en contacto contigo muy pronto.
              </Typography>

              <Grid container spacing={isMobile ? 1 : 2}>
                {[
                  { name: 'nombre', label: 'Nombre', xs: 12 },
                  { name: 'telefono', label: 'Teléfono', xs: 12, sm: 6 },
                  { name: 'correo', label: 'Correo', xs: 12, sm: 6 },
                  { name: 'empresa', label: 'Empresa', xs: 12, sm: 6 },
                  { name: 'puesto', label: 'Puesto', xs: 12, sm: 6 },
                  { name: 'pais', label: 'País', xs: 12, sm: 6 },
                  { name: 'estado', label: 'Estado', xs: 12, sm: 6 },
                  { name: 'conociste', label: '¿Cómo nos conociste?', xs: 12 },
                ].map(({ name, label, xs, sm }) => (
                  <Grid item xs={xs} sm={sm} key={name}>
                    <TextField
                      fullWidth
                      name={name}
                      placeholder={label}
                      value={formData[name]}
                      onChange={handleChange}
                      error={Boolean(errors[name])}
                      helperText={errors[name] || ''}
                      variant="standard"
                      InputProps={{
                        disableUnderline: true,
                        sx: {
                          backgroundColor: '#ffffff33',
                          borderRadius: '20px',
                          boxShadow: '4px 4px 8px rgba(0,0,0,0.25)',
                          '& .MuiInputBase-input': {
                            paddingY: isMobile ? '10px' : '12px',
                            paddingX: '20px',
                            fontSize: isMobile ? '13px' : '14px',
                            fontFamily: 'Poppins, sans-serif',
                            color: '#000',
                          },
                        },
                      }}
                    />
                  </Grid>
                ))}
              </Grid>

              {/* Checkbox + botón alineados horizontalmente */}
              <Box
                mt={4}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap={isMobile ? 'wrap' : 'nowrap'}
                gap={2}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    component="input"
                    type="checkbox"
                    checked={aceptaAviso}
                    onChange={() => {
                      setAceptaAviso(!aceptaAviso);
                      setAvisoError(false);
                    }}
                    sx={{
                      width: 18,
                      height: 18,
                      border: '1px solid #000',
                      borderRadius: '2px',
                      cursor: 'pointer',
                      backgroundColor: 'transparent',
                      appearance: 'none',
                      position: 'relative',
                      '&:checked::before': {
                        content: '"✓"',
                        position: 'absolute',
                        top: '-2px',
                        left: '3px',
                        fontSize: '14px',
                        color: '#127A8B',
                      },
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: isMobile ? '12px' : '14px',
                      color: avisoError ? 'red' : '#000',
                    }}
                  >
                    He leído y acepto el{' '}
                    <Box
                      component="a"
                      href="https://ebmconsultores.com.mx/aviso-de-privacidad/"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        fontWeight: 'bold',
                        textDecoration: 'none',
                        color: avisoError ? 'red' : '#38383D',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      Aviso de privacidad
                    </Box>
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  sx={{
                    borderRadius: '50px',
                    backgroundColor: '#F2F2F2',
                    color: '#127A8B',
                    fontWeight: 'bold',
                    fontSize: isMobile ? '13px' : '14px',
                    paddingX: 4,
                    paddingY: 1,
                    fontFamily: 'Poppins, sans-serif',
                    boxShadow: '4px 4px 8px rgba(0, 0, 0, 0.25)',
                    '&:hover': {
                      backgroundColor: '#e0e0e0',
                    },
                    minWidth: isMobile ? '200px' : 'auto'
                  }}
                >
                  Enviar y suscribirse
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal >

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          ¡Gracias por suscribirte! Ahora puedes descargar los recursos.
        </Alert>
      </Snackbar>
    </>
  );
}
